import type { DocMode } from '@blocksuite/blocks';
import { CUTOFF_WIDTH } from '@pique/core/components/app-sidebar';
import {
  appSidebarOpenAtom,
  appSidebarWidthAtom,
} from '@pique/core/components/app-sidebar/index.jotai';
import { Unreachable } from '@pique/env/constant';
import {
  DocsService,
  Entity,
  getCurrentStore,
  LiveData,
} from '@toeverything/infra';
import { type To } from 'history';
import { nanoid } from 'nanoid';
import { BehaviorSubject } from 'rxjs';

import type { WorkbenchNewTabHandler } from '../services/workbench-new-tab-handler';
import type { WorkbenchDefaultState } from '../services/workbench-view-state';
import { View } from './view';
import { canUseEdgelessSplit } from './workbench-layout-policy';

export type WorkbenchPosition = 'beside' | 'active' | 'head' | 'tail' | number;

type WorkbenchOpenOptions = {
  at?: WorkbenchPosition | 'new-tab';
  replaceHistory?: boolean;
  show?: boolean; // only for new tab
  viewMode?: DocMode; // new option for controlling view mode
  onComplete?: () => void; // callback after navigation and view setup is complete
};

export class Workbench extends Entity {
  constructor(
    private readonly defaultState: WorkbenchDefaultState,
    private readonly newTabHandler: WorkbenchNewTabHandler
  ) {
    super();
  }

  readonly activeViewIndex$ = new LiveData(this.defaultState.activeViewIndex);
  readonly basename$ = new LiveData(this.defaultState.basename);

  readonly views$: LiveData<View[]> = new LiveData(
    this.defaultState.views.map(meta => {
      return this.framework.createEntity(View, {
        id: meta.id,
        defaultLocation: meta.path,
        icon: meta.icon,
        title: meta.title,
      });
    })
  );

  activeView$ = LiveData.computed(get => {
    const activeIndex = get(this.activeViewIndex$);
    const views: View[] = get(this.views$);
    return views[activeIndex];
  });
  location$ = LiveData.computed((get: <L>(data: LiveData<L>) => L) => {
    const activeView = get(this.activeView$);
    if (!activeView) {
      return {
        pathname: '/',
        search: '',
        hash: '',
        state: null,
        key: 'default',
      };
    }
    return get(activeView.location$);
  });
  sidebarOpen$ = new LiveData(false);
  chatPanelVisible$ = new BehaviorSubject<boolean>(false);

  active(index: number) {
    index = Math.max(0, Math.min(index, this.views$.value.length - 1));
    this.activeViewIndex$.next(index);
  }

  updateBasename(basename: string) {
    this.basename$.next(basename);
  }

  createView(at: WorkbenchPosition = 'beside', defaultLocation: To) {
    const view = this.framework.createEntity(View, {
      id: nanoid(),
      defaultLocation,
    });
    const newViews = [...this.views$.value];
    newViews.splice(this.indexAt(at), 0, view);
    this.views$.next(newViews);
    const index = newViews.indexOf(view);
    this.active(index);
    return index;
  }

  openSidebar() {
    if (!canUseEdgelessSplit()) {
      return;
    }
    this.sidebarOpen$.next(true);
  }

  closeSidebar() {
    this.sidebarOpen$.next(false);
  }

  toggleSidebar() {
    if (!canUseEdgelessSplit()) {
      this.closeSidebar();
      return;
    }
    this.sidebarOpen$.next(!this.sidebarOpen$.value);
  }

  open(to: To, option: WorkbenchOpenOptions = {}) {
    if (option.at === 'new-tab') {
      this.newTab(to, {
        show: option.show,
      });
    } else {
      const {
        at = 'active',
        replaceHistory = false,
        viewMode: requestedViewMode,
        onComplete,
      } = option;
      const viewMode = requestedViewMode;
      const supportsEdgelessSplit = canUseEdgelessSplit();
      let view = supportsEdgelessSplit
        ? this.viewAt(at)
        : (this.activeView$.value ?? this.viewAt('active'));

      // Pique Update - Handle view mode specific logic before creating/updating views
      if (
        supportsEdgelessSplit &&
        viewMode === 'edgeless' &&
        this.views$.value.length >= 2
      ) {
        // Try to find existing edgeless view first
        view =
          this.views$.value.find(
            (v: View) => v.modeChange$.value === 'edgeless'
          ) ?? this.views$.value[this.views$.value.length - 1];
      }

      // Pique Update - For non edgeless requests, if more than 1 views, close all inactive. This is probably a request to open a non doc route
      if (
        supportsEdgelessSplit &&
        viewMode !== 'edgeless' &&
        this.views$.value.length > 1
      ) {
        const inactiveViews = this.views$.value.filter(
          (v: View) => v.id !== view?.id
        );
        inactiveViews.forEach((v: View) => this.close(v));
      }

      if (!view) {
        const newIndex = this.createView(at, to);
        view = this.viewAt(newIndex);

        if (!view) {
          throw new Unreachable();
        }
      } else {
        if (replaceHistory) {
          view.history.replace(to);
        } else {
          view.history.push(to);
        }
      }

      if (!supportsEdgelessSplit) {
        this.closeOthers(view);
      }

      // Pique Update - Set view mode to 'page' if there's only one view, otherwise let the caller decide
      const modeToSet = !supportsEdgelessSplit
        ? 'page'
        : this.views$.value.length <= 1
          ? 'page'
          : viewMode;

      if (modeToSet) {
        view.setViewMode(modeToSet);
      }

      // collapse the right sidebar
      this.closeSidebar();

      // Call completion callback after navigation setup is done
      if (onComplete) {
        // Wait longer for the actual page editor to load and render
        setTimeout(() => onComplete(), 1000);
      }
    }
  }

  newTab(
    to: To,
    {
      show,
    }: {
      show?: boolean;
    } = {}
  ) {
    this.newTabHandler({
      basename: this.basename$.value,
      to,
      show: show ?? true,
    });
  }

  openDoc(
    id: string | { docId: string; blockId?: string; mode?: DocMode },
    options?: WorkbenchOpenOptions
  ) {
    const docId = typeof id === 'string' ? id : id.docId;
    const blockId = typeof id === 'string' ? undefined : id.blockId;
    const mode = typeof id === 'string' ? undefined : id.mode;
    const hash = blockId ? `#${blockId}` : '';
    const viewMode = canUseEdgelessSplit() ? mode : undefined;
    const query = viewMode ? `?mode=${viewMode}` : '';

    // Pass mode through to open() if specified
    this.open(`/${docId}${query}${hash}`, {
      ...options,
      viewMode,
    });
  }

  openCollections(options?: WorkbenchOpenOptions) {
    this.open('/collection', options);
  }

  openCollection(collectionId: string, options?: WorkbenchOpenOptions) {
    this.open(`/collection/${collectionId}`, options);
  }

  openAll(options?: WorkbenchOpenOptions) {
    this.open('/all', options);
  }

  openTrash(options?: WorkbenchOpenOptions) {
    this.open('/trash', options);
  }

  openTags(options?: WorkbenchOpenOptions) {
    this.open('/tag', options);
  }

  openTag(tagId: string, options?: WorkbenchOpenOptions) {
    this.open(`/tag/${tagId}`, options);
  }

  viewAt(positionIndex: WorkbenchPosition): View | undefined {
    return this.views$.value[this.indexAt(positionIndex)];
  }

  close(view: View) {
    if (this.views$.value.length === 1) return;
    const index = this.views$.value.indexOf(view);
    if (index === -1) return;
    const newViews = [...this.views$.value];
    newViews.splice(index, 1);
    const activeViewIndex = this.activeViewIndex$.value;
    if (activeViewIndex !== 0 && activeViewIndex >= index) {
      this.active(activeViewIndex - 1);
    }
    this.views$.next(newViews);
  }

  closeOthers(view: View) {
    view.size$.next(100);
    this.views$.next([view]);
    this.active(0);
  }

  moveView(from: number, to: number) {
    from = Math.max(0, Math.min(from, this.views$.value.length - 1));
    to = Math.max(0, Math.min(to, this.views$.value.length - 1));
    if (from === to) return;
    const views = [...this.views$.value];
    const fromView = views[from];
    const toView = views[to];
    if (!fromView || !toView) return;
    views[to] = fromView;
    views[from] = toView;
    this.views$.next(views);
    this.active(to);
  }

  /**
   * resize specified view and the next view
   * @param view
   * @param percent from 0 to 1
   * @returns
   */
  resize(index: number, percent: number) {
    const view = this.views$.value[index];
    const nextView = this.views$.value[index + 1];
    if (!view || !nextView) return;

    const totalViewSize = this.views$.value.reduce(
      (sum: number, v: View) => sum + v.size$.value,
      0
    );
    const percentOfTotal = totalViewSize * percent;
    const newSize = Number((view.size$.value + percentOfTotal).toFixed(4));
    const newNextSize = Number(
      (nextView.size$.value - percentOfTotal).toFixed(4)
    );
    if (newSize / totalViewSize < 0.3 || newNextSize / totalViewSize < 0.3)
      return;
    view.setSize(newSize);
    nextView.setSize(newNextSize);
  }

  toggleChatPanel = (visible?: boolean) => {
    const newValue = visible ?? !this.chatPanelVisible$.value;
    this.chatPanelVisible$.next(newValue);
  };

  private indexAt(positionIndex: WorkbenchPosition): number {
    if (positionIndex === 'active') {
      return this.activeViewIndex$.value;
    }
    if (positionIndex === 'beside') {
      return this.activeViewIndex$.value + 1;
    }
    if (positionIndex === 'head') {
      return 0;
    }
    if (positionIndex === 'tail') {
      return this.views$.value.length;
    }
    return positionIndex;
  }

  collapseLeftSidebar() {
    const store = getCurrentStore();
    (store.set as (atom: typeof appSidebarOpenAtom, value: boolean) => void)(
      appSidebarOpenAtom,
      false
    );
  }

  expandLeftSidebar() {
    const store = getCurrentStore();
    (store.set as (atom: typeof appSidebarWidthAtom, value: number) => void)(
      appSidebarWidthAtom,
      CUTOFF_WIDTH
    );
    (store.set as (atom: typeof appSidebarOpenAtom, value: boolean) => void)(
      appSidebarOpenAtom,
      true
    );
  }

  openCurrentDocBeside() {
    if (!canUseEdgelessSplit()) {
      return;
    }

    if (this.views$.value.length >= 2) {
      this.setFirstViewToEdgeless();
      return;
    }
    const activeView = this.activeView$.value;
    if (!activeView) return;
    const currentPath = activeView.location$.value.pathname;

    const match = currentPath.match(/\/([^/?#]+)/);
    if (!match) return;

    const potentialDocId = match[1];
    if (!potentialDocId) return;

    const docsService = this.framework.get(DocsService);
    const docRecord = docsService.list.doc$(potentialDocId).value;

    if (docRecord) {
      this.collapseLeftSidebar();
      this.openDoc({ docId: potentialDocId, mode: 'page' }, { at: 'tail' });
      this.setFirstViewToEdgeless();
    }
  }

  setFirstViewToEdgeless() {
    if (!canUseEdgelessSplit()) {
      return;
    }

    if (
      this.views$.value.some((v: View) => v.modeChange$.value === 'edgeless')
    ) {
      return;
    }

    const firstView = this.views$.value[0];
    if (!firstView) {
      return;
    }

    firstView.setViewMode('edgeless');

    const secondView = this.views$.value[1];
    if (secondView) {
      firstView.setSize(0);
      secondView.setSize(100);

      requestAnimationFrame(() => {
        const view0 = this.views$.value[0];
        const view1 = this.views$.value[1];
        view0?.animateSize(35);
        view1?.animateSize(65);
      });
    }
  }

  toggleEdgelessView() {
    if (!canUseEdgelessSplit()) {
      return;
    }

    if (this.views$.value.length > 1) {
      const edgelessView = this.views$.value.find(
        (v: View) => v.modeChange$.value === 'edgeless'
      );
      if (edgelessView) {
        this.close(edgelessView);
      }
    } else {
      this.openCurrentDocBeside();
      this.setFirstViewToEdgeless();
    }
  }

  /**
   * Animates the first view to take 65% of space and second view 35%
   * This is the opposite of setFirstViewToEdgeless() (which does 35%/65%)
   */
  setFirstViewToLarge(firstViewSize: number = 60, secondViewSize: number = 40) {
    if (this.views$.value.length < 2) {
      return;
    }
    requestAnimationFrame(() => {
      const view0 = this.views$.value[0];
      const view1 = this.views$.value[1];
      view0?.animateSize(firstViewSize);
      view1?.animateSize(secondViewSize);
    });
  }
}
